import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, icon, children, className, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
          'transition-all duration-150 ease-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-nord-8/50 focus-visible:ring-offset-2 focus-visible:ring-offset-nord-0',
          'disabled:opacity-50 disabled:pointer-events-none',
          'active:scale-[0.97] active:transition-none',
          {
            'bg-nord-10 text-white hover:bg-nord-9 shadow-sm hover:shadow-glow-sm': variant === 'primary',
            'bg-nord-2 text-nord-5 hover:bg-nord-3 border border-nord-3/60 hover:border-nord-3': variant === 'secondary',
            'text-nord-4 hover:bg-nord-2 hover:text-nord-5': variant === 'ghost',
            'bg-nord-11 text-white hover:bg-nord-11/90 shadow-sm': variant === 'danger',
          },
          {
            'px-2.5 py-1.5 text-xs': size === 'sm',
            'px-4 py-2 text-sm': size === 'md',
            'px-6 py-2.5 text-base': size === 'lg',
            'h-8 w-8 p-0': size === 'icon',
          },
          className,
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className={clsx('animate-spin', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
        ) : icon ? (
          <span className="shrink-0">{icon}</span>
        ) : null}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
